import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';

const EASE = [0.16, 1, 0.3, 1];

/* ─────────────────────────────────────────────────────────────
   The parts the module screens are built from.

   The original six screens each grew their own card, grid and form
   classes. The module screens share one set instead, so thirty of them
   read as one product: a panel, a status chip, a row list, a KPI strip,
   a segmented filter and a confirmation line.
   ───────────────────────────────────────────────────────────── */

/* A status label. `tone` is one of ok, warn, alert, info or muted. */
export function Chip({ tone = 'muted', children }) {
    return <span className={`chip chip--${tone}`}>{children}</span>;
}

/* The figures strip at the top of a staff screen. */
export function Kpis({ items }) {
    return (
        <div className="kpis">
            {items.map((item, i) => (
                <Panel key={item.label} index={i} className={`kpi${item.tone ? ` kpi--${item.tone}` : ''}`}>
                    <span className="kpi__label">{item.label}</span>
                    <span className="kpi__value">{item.value}</span>
                    {item.note && <span className="kpi__note">{item.note}</span>}
                </Panel>
            ))}
        </div>
    );
}

/* A card that rises in after the screen heading, like Rise does. */
export function Panel({ index = 0, className = '', as = 'div', children, ...rest }) {
    const reduced = useReducedMotion();
    const Tag = motion[as] || motion.div;

    return (
        <Tag
            className={`panel ${className}`.trim()}
            initial={reduced ? false : { opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: Math.min(0.08 + index * 0.05, 0.5), ease: EASE }}
            {...rest}
        >
            {children}
        </Tag>
    );
}

/* A row of mutually exclusive options: a filter or a tab strip. */
export function Segments({ options, value, onChange, label }) {
    return (
        <div className="segments" role="group" aria-label={label}>
            {options.map(option => {
                const name = typeof option === 'string' ? option : option.value;
                const text = typeof option === 'string' ? option : option.label;

                return (
                    <button
                        type="button"
                        key={name}
                        className={`segments__item${value === name ? ' segments__item--on' : ''}`}
                        aria-pressed={value === name}
                        onClick={() => onChange(name)}
                    >
                        {text}
                    </button>
                );
            })}
        </div>
    );
}

/* A labelled control. The label wraps the control, so no ids to invent. */
export function Field({ label, children, wide }) {
    return (
        <label className={`field${wide ? ' field--wide' : ''}`}>
            <span className="form-label">{label}</span>
            {children}
        </label>
    );
}

/* Horizontal bars for a small comparison, labelled with their values. */
export function Bars({ items, unit = '' }) {
    const max = Math.max(...items.map(i => i.value), 1);

    return (
        <ul className="bars">
            {items.map(item => (
                <li className="bars__row" key={item.label}>
                    <span className="bars__label">{item.label}</span>
                    <span className="bars__track">
                        <span className="bars__fill" style={{ width: `${(item.value / max) * 100}%` }} />
                    </span>
                    <span className="bars__value">{item.value}{unit}</span>
                </li>
            ))}
        </ul>
    );
}

/* An empty list says so, rather than leaving a hole. */
export function Empty({ children }) {
    return <p className="empty">{children}</p>;
}

/* A confirmation that appears after an action and clears itself. */
export function useFlash() {
    const [message, setMessage] = useState(null);
    const timer = useRef(null);

    useEffect(() => () => clearTimeout(timer.current), []);

    const flash = useCallback(text => {
        clearTimeout(timer.current);
        setMessage(text);
        timer.current = setTimeout(() => setMessage(null), 4500);
    }, []);

    const node = (
        <div className="flash-slot" role="status" aria-live="polite">
            <AnimatePresence>
                {message && (
                    <motion.p
                        className="flash"
                        key={message}
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0 }}
                        transition={{ duration: 0.35, ease: EASE }}
                    >
                        {message}
                    </motion.p>
                )}
            </AnimatePresence>
        </div>
    );

    return [node, flash];
}

/* Reads a submitted form into an object and clears it for the next one.
   Returns null if a required value is missing, so the caller can bail. */
export function readForm(event, required = []) {
    event.preventDefault();
    const form = event.currentTarget;
    const values = Object.fromEntries(new FormData(form).entries());

    for (const key of required) {
        if (!String(values[key] || '').trim()) return null;
    }

    form.reset();
    return values;
}

/* ─────────────────────────────────────────────────────────────
   Modal: the one overlay every screen uses for details, confirmations
   and small forms. A centred card on wide screens, a sheet that rises
   from the bottom on phones. Rendered into <body> so a transformed
   ancestor (every screen is one, mid-transition) cannot clip it.

     <Modal open={open} onClose={close} eyebrow="Pool" title="Cabana 3"
            actions={<button className="btn btn-primary">Reserve</button>}>
       ...body...
     </Modal>

   `size` is 'md' (default), 'lg' or 'full'. Escape and a tap on the
   backdrop both close it; `dismissable={false}` turns both off for a
   confirmation that must be answered.
   ───────────────────────────────────────────────────────────── */
export function Modal({ open, onClose, eyebrow, title, actions, size = 'md', dismissable = true, children }) {
    const reduced = useReducedMotion();

    useEffect(() => {
        if (!open || !dismissable) return undefined;
        const onKey = event => { if (event.key === 'Escape') onClose?.(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, dismissable, onClose]);

    if (typeof document === 'undefined') return null;

    return createPortal(
        <AnimatePresence>
            {open && (
                <motion.div
                    className="modal"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.25 }}
                    onPointerDown={event => { if (dismissable && event.target === event.currentTarget) onClose?.(); }}
                >
                    <motion.div
                        className={`modal__card modal__card--${size}`}
                        role="dialog"
                        aria-modal="true"
                        aria-label={typeof title === 'string' ? title : undefined}
                        initial={reduced ? false : { opacity: 0, y: 40, scale: 0.98 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, y: 30, scale: 0.98 }}
                        transition={{ duration: 0.45, ease: EASE }}
                    >
                        <div className="modal__head">
                            <div>
                                {eyebrow && <span className="screen__eyebrow">{eyebrow}</span>}
                                {title && <h3 className="modal__title">{title}</h3>}
                            </div>
                            {dismissable && (
                                <button type="button" className="modal__close" onClick={onClose} aria-label="Close">
                                    <span aria-hidden="true">&times;</span>
                                </button>
                            )}
                        </div>
                        <div className="modal__body screen-content">{children}</div>
                        {actions && <div className="modal__actions screen-content">{actions}</div>}
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>,
        document.body
    );
}

/* Open/close state for a Modal, optionally carrying the item it shows. */
export function useModal() {
    const [item, setItem] = useState(null);
    const open = useCallback((value = true) => setItem(value), []);
    const close = useCallback(() => setItem(null), []);
    return { item, isOpen: item !== null, open, close };
}
